import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { List, Map } from 'immutable';
import classNames from 'classnames';

import { UserFilterAutocomplete } from '../UserFilterAutocomplete';
import Checkbox from '../../helpers/Checkbox';
import Button from '../../helpers/Button';
import Select from '../../helpers/Select';
import { getModuleSuggestions, getSupervisorSuggestions } from '../../../utils/userSuggestions';

import style from './style.module.css';

const CLEARED_FILTERS = {
  includeInactive: false,
  search: '',
  categoryTitle: '',
  roleId: '',
  module: '',
  moduleId: '',
  supervisor: '',
  supervisorId: '',
};

/**
 * Filters of the user list.
 *
 * @param {object} props Props.
 * @param {Immutable.Map} props.filters Applied user list filters.
 * @param {Immutable.List} props.roles List of roles.
 * @param {Immutable.List} props.dashboardUserTitles List of title categories.
 * @param {Immutable.List} props.suggestionUsers Users for module/supervisor suggestions.
 * @param {Function} props.onFiltersChange Handle filters change, called with changed filters.
 */
const UserListFiltersComponent = ({ filters, roles, dashboardUserTitles, suggestionUsers, onFiltersChange }) => {
  const appliedSearch = filters.get('search');
  const includeInactive = filters.get('includeInactive');
  const [searchText, setSearchText] = useState(appliedSearch);

  useEffect(() => {
    setSearchText(appliedSearch);
  }, [appliedSearch]);

  const handleIncludeInactiveChange = useCallback(
    event => {
      onFiltersChange({ includeInactive: event.target.checked });
    },
    [onFiltersChange],
  );

  const handleSearchTextChange = useCallback(event => {
    setSearchText(event.target.value);
  }, []);

  const handleSearchClick = useCallback(() => {
    onFiltersChange({ search: searchText.trim() });
  }, [searchText, onFiltersChange]);

  const handleSearchKeyDown = useCallback(
    event => {
      if (event.key !== 'Enter') return;

      onFiltersChange({ search: searchText.trim() });
    },
    [searchText, onFiltersChange],
  );

  const handleClearClick = useCallback(() => {
    setSearchText('');
    onFiltersChange(CLEARED_FILTERS);
  }, [onFiltersChange]);

  const handleSelectChange = useCallback(
    event => {
      const { name, value } = event.target;

      onFiltersChange({ [name]: value });
    },
    [onFiltersChange],
  );

  return (
    <div className={style.host}>
      <Checkbox
        key={String(includeInactive)}
        defaultChecked={includeInactive}
        id="display-inactive"
        label="Display Inactive"
        name="includeInactive"
        onChange={handleIncludeInactiveChange}
      />
      <div className={classNames(style.search, 'input-group')}>
        <input
          className="form-control input-sm"
          name="search"
          onChange={handleSearchTextChange}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search by First/Last Name, Username, Email"
          type="text"
          value={searchText}
        />
        <span className="input-group-btn">
          <Button className="btn-primary" onClick={handleSearchClick}>
            <i className="fa fa-search" /> Search
          </Button>
        </span>
      </div>
      <Select
        className="input-sm"
        defaultText="Select Title Category..."
        id="titleCategoryFilter"
        label="Title Category"
        name="categoryTitle"
        nameKey="title"
        onChange={handleSelectChange}
        options={dashboardUserTitles}
        value={filters.get('categoryTitle')}
        valueKey="title"
        wrapperClassName={style.field}
      />
      <Select
        className="input-sm"
        defaultText="Select Roles..."
        id="roleFilter"
        label="Role"
        name="roleId"
        onChange={handleSelectChange}
        options={roles}
        value={filters.get('roleId')}
        valueKey="id"
        wrapperClassName={style.field}
      />
      <div className={style.field}>
        <UserFilterAutocomplete
          getSuggestions={getModuleSuggestions}
          idName="moduleId"
          name="module"
          onChange={onFiltersChange}
          placeholder="Module"
          users={suggestionUsers}
          value={filters.get('module')}
        />
      </div>
      <div className={style.field}>
        <UserFilterAutocomplete
          getSuggestions={getSupervisorSuggestions}
          idName="supervisorId"
          name="supervisor"
          onChange={onFiltersChange}
          placeholder="Supervisor"
          users={suggestionUsers}
          value={filters.get('supervisor')}
        />
      </div>
      <Button onClick={handleClearClick}>Clear</Button>
    </div>
  );
};

UserListFiltersComponent.propTypes = {
  dashboardUserTitles: PropTypes.instanceOf(List).isRequired,
  filters: PropTypes.instanceOf(Map).isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  roles: PropTypes.instanceOf(List).isRequired,
  suggestionUsers: PropTypes.instanceOf(List).isRequired,
};

export const UserListFilters = memo(UserListFiltersComponent);
